import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, Sparkles, FileEdit, Save, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, fmtTime, fmtUSD, streamSSE, safe } from "@/lib/api";
import { RichText } from "@/components/meridian/RichText";
import { RaiseIssue } from "./RaiseIssue";
import { aiCue } from "@/lib/cues";

const SEV = { High: "text-red-700 border-red-500/50 bg-red-500/10", Medium: "text-amber-700 border-amber-500/40", Low: "text-slate-700 border-slate-300" };

export function AlertsPanel({ alerts, onOpen, notesMode }) {
  return (
    <div className="p-6 space-y-2" data-testid={notesMode ? "notes-list" : "alerts-list"}>
      {notesMode && <div className="eyebrow mb-3">Investigation note drafts - pending human review</div>}
      {!alerts.length && <div className="panel p-8 text-sm text-slate-500">{notesMode ? "No drafts yet. Open an AML alert and draft an investigation note." : "No alerts."}</div>}
      {alerts.map((a) => (
        <button key={a.id} data-testid={`alert-item-${a.id}`} onClick={() => onOpen(a.id)} className="rise w-full panel p-4 text-left flex flex-wrap gap-4 items-center hover:border-sky-500/50 transition-colors">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${SEV[a.severity]}`}>{a.severity}</span>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm text-slate-900"><span className="num text-slate-500 mr-2">{a.id}</span>{a.rule_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{a.wire.bank} · {a.wire.counterparty} ({a.wire.country}) · {fmtTime(a.triggered_at)}</div>
            {notesMode && <div className="text-[11px] text-amber-700 mt-1">{a.note.status}</div>}
          </div>
          <span className="num text-sm text-slate-900">{fmtUSD(a.wire.amount)}</span>
          <span className="text-[11px] text-slate-600 w-28 text-right">{a.status}{a.grc_issue_id && <span className="block text-[10px] text-red-700 num" data-testid={`alert-issue-badge-${a.id}`}>{a.grc_issue_id}</span>}</span>
        </button>
      ))}
    </div>
  );
}

function useStream(alertId, kind, onChanged) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setText("");
    const cue = aiCue(`${kind === "explain" ? "Explain alert" : "Draft investigation note for"} ${alertId}`);
    try {
      await streamSSE(`/alerts/${alertId}/${kind}`, {}, (ev) => {
        if (ev.type === "delta") { cue.delta(ev.content); setText((t) => t + ev.content); }
        if (ev.type === "error") toast.error(ev.content);
      });
      await onChanged();
    } catch (e) { if (!String(e.message).startsWith("Blocked")) toast.error("AI request failed - retry"); }
    cue.done();
    setBusy(false);
  };
  return { text, setText, busy, run };
}

function AlertBody({ alert, onChanged }) {
  const ex = useStream(alert.id, "explain", onChanged);
  const note = useStream(alert.id, "note", onChanged);
  const [editing, setEditing] = useState(false);
  useEffect(() => { ex.setText(alert.explanation || ""); note.setText(alert.note?.content || ""); }, [alert.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const w = alert.wire;
  const save = safe(async () => { await api.put(`/alerts/${alert.id}/note`, { content: note.text }); await onChanged(); setEditing(false); toast.success("Draft saved - no disposition made"); });
  return (
    <div className="space-y-5 mt-4">
      <div className="panel p-4">
        <div className="eyebrow mb-1">Rule fired · {alert.rule_code}</div>
        <div className="text-sm text-slate-900">{alert.rule_name}</div>
        <p className="text-xs text-slate-600 mt-1" data-testid="alert-rule-description">{alert.rule_description}</p>
      </div>
      <div className="grid grid-cols-2 gap-px bg-slate-100 rounded overflow-hidden border border-slate-200" data-testid="alert-data-points">
        {[["Wire", w.id], ["Amount", fmtUSD(w.amount)], ["Bank", w.bank], ["Counterparty", w.counterparty], ["Country", w.country], ["Status", w.status], ...alert.data_points.map((d) => [d.label, d.value])].map(([l, v]) => (
          <div key={l} className="bg-white px-3 py-2"><div className="text-[10px] text-slate-500">{l}</div><div className="num text-xs text-slate-900 mt-0.5">{v}</div></div>
        ))}
      </div>
      <div>
        <div className="flex justify-between items-center mb-2">
          <span className="eyebrow">AI explanation</span>
          <button data-testid="alert-explain-button" disabled={ex.busy} onClick={ex.run} className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded bg-sky-600 text-white hover:bg-sky-700 transition-colors disabled:opacity-50">
            {ex.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{ex.text ? "Re-explain" : "Explain"}
          </button>
        </div>
        <div className="panel p-4 min-h-[60px]" data-testid="alert-explanation">{ex.text ? <RichText text={ex.text} /> : <span className="text-xs text-slate-500">Ask the AI to explain this alert in plain language.</span>}</div>
      </div>
      <div>
        <div className="flex justify-between items-center mb-2">
          <span className="eyebrow">Investigation note</span>
          <div className="flex gap-2">
            {note.text && !note.busy && <button data-testid="note-edit-toggle" onClick={() => setEditing((e) => !e)} className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-slate-700"><Pencil className="w-3 h-3" />{editing ? "Preview" : "Edit"}</button>}
            {editing && <button data-testid="note-save-button" onClick={save} className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-emerald-500/50 text-emerald-700"><Save className="w-3 h-3" />Save</button>}
            <button data-testid="alert-draft-note-button" disabled={note.busy} onClick={() => { setEditing(false); note.run(); }} className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded border border-sky-500/50 text-sky-700 hover:bg-sky-600/10 transition-colors disabled:opacity-50">
              {note.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileEdit className="w-3.5 h-3.5" />}{note.text ? "Redraft" : "Draft note"}
            </button>
          </div>
        </div>
        {editing ? <textarea data-testid="note-editor" value={note.text} onChange={(e) => note.setText(e.target.value)} className="w-full h-64 bg-slate-50 border border-slate-300 rounded p-3 num text-xs text-slate-800 outline-none" />
          : <div className="panel p-4 min-h-[60px]" data-testid="alert-note">{note.text ? <RichText text={note.text} /> : <span className="text-xs text-slate-500">No draft yet.</span>}</div>}
      </div>
      <RaiseIssue alert={alert} onChanged={onChanged} />
    </div>
  );
}

export function AlertDrawer({ alert, onClose, onChanged }) {
  return (
    <Sheet open={!!alert} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="alert-drawer" className="w-full sm:max-w-xl bg-white border-slate-300 overflow-y-auto thin-scroll">
        {alert && (
          <>
            <SheetHeader>
              <SheetTitle className="font-display flex items-center gap-2 text-slate-900"><ShieldAlert className="w-4 h-4 text-red-600" />{alert.id}<span className={`text-[10px] px-1.5 py-0.5 rounded border ${SEV[alert.severity]}`}>{alert.severity}</span></SheetTitle>
              <SheetDescription className="text-xs text-amber-700">Nothing is resolved automatically - a human investigator decides.</SheetDescription>
            </SheetHeader>
            <AlertBody alert={alert} onChanged={onChanged} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
